import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { countryList, deliveryPromiseSettings, LINKED_CALENDAR_DEFAULT_OPTION, weekendDays } from '../../../configs';
import { deliveryPromiseSelectOptions } from '../../../types';
import { convertDeliveryPromiseValues, convertDeliveryPromiseType } from '../../../utils/string.utils';
import { ShippingMethod } from '@pages/checkout-app/types/types';
import { ICheckoutEstimatedTimeConversion, ICheckoutVariantABTest } from '@pdqdev/pdq-shared/dist/interfaces';
import { takeUntil } from 'rxjs/operators';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Subject, Subscription } from 'rxjs';
import { Carrier } from '@services/carriers/carrier.model';
import { carrierSupportSchedulePickup, carrierSupportsLiveETA } from '@pages/checkout-app/utils/carrier.utils';
import { Shop } from '@services/shops/shop.model';
import { WEEKDAY_OPTIONS } from '@pages/checkout-app/components/fee-per-weekday/fee-per-weekday.component';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';

const LiveEstimatedConverisonOption = {
  text: 'Live estimated time of arrival',
  value: ICheckoutEstimatedTimeConversion.LIVE,
  checked: false,
};

const ManualEstimatedConversionOption = {
  text: 'Fixed promise',
  value: ICheckoutEstimatedTimeConversion.MANUAL,
  checked: false,
};

const PDQIQEstimatedConversionOption = {
  text: 'MAXX Promise',
  value: ICheckoutEstimatedTimeConversion.PDQIQ,
  subText: 'Uses historical data for more accurate estimates',
  checked: false,
  labelIcon: 'assets/icons/maxx-promise-logo.png',
};

const LinkedCalendarEstimatedConversionOption = {
  text: 'Linked calendar',
  value: ICheckoutEstimatedTimeConversion.LINKED_CALENDAR,
  checked: false,
};

const CATEGORIES_SUPPORTING_LINKED_DATE_PICKER_PROFILE = [
  ShippingMethod.DOMESTIC,
  ShippingMethod.LOCAL,
  ShippingMethod.DOMESTIC_LIVE_RATE,
  ShippingMethod.FIXED_LIVE_RATE,
  ShippingMethod.INTERNATIONAL_FIXED,
  ShippingMethod.INTERNATIONAL_LIVE_RATE,
  ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE,
  ShippingMethod.STORE_PICK_UP,
];

const DELIVERY_PROMISE_PDQ_IQ_PERCENTILE_VALUES = [25, 50, 80, 90, 95, 99];

const CUSTOM_DATE_VALUE = 10;

@Component({
  selector: 'app-delivery-promise',
  templateUrl: './delivery-promise.component.html',
  styleUrls: ['./delivery-promise.component.scss'],
  standalone: false,
})
export class DeliveryPromiseComponent implements OnInit, OnChanges {
  @Input() formGroup: UntypedFormGroup;
  @Input() deliveryType: string;
  @Input() shippingMethodCategory: ShippingMethod;
  @Input() googleCalendarIdsList: any;
  @Input() showExpansionGuide: string;
  @Input() accordionActiveSection: string[];
  @Input() activeCarrier: Carrier;
  @Input() runningABTest: ICheckoutVariantABTest | null;
  @Input() shop: Shop;
  @Input() linkedDatePickerExtensionOptions: DropdownOption[] = [];
  @Input() multiShipDatePickerProfileOptions: DropdownOption[] = [];
  @Output() onOpenExpansionEmit = new EventEmitter<string>();
  @Output() onCloseExpansionEmit = new EventEmitter<void>();
  @Output() onExpandedNeededEmit = new EventEmitter<string>();
  @onDestroyComplete() destroy$ = new Subject();
  readonly ShippingMethod = ShippingMethod;
  readonly deliveryPromisePdqIqPercentileOptions = DELIVERY_PROMISE_PDQ_IQ_PERCENTILE_VALUES.map((value) => ({
    value,
    label: `${value}th`,
  }));
  // without custom date
  readonly weekdayOptions = WEEKDAY_OPTIONS.filter((o) => o.value !== CUSTOM_DATE_VALUE).map((o) => {
    return { name: o.label, value: o.value };
  });
  isCarrierSupportSchedulePickup: boolean;
  carrierSupportsLiveETA: boolean;
  deliveryPromiseSettings = [...deliveryPromiseSettings];
  deliveryPromiseTypeSelect: deliveryPromiseSelectOptions;
  deliveryPromiseValue: deliveryPromiseSelectOptions;
  showDeliveryPromiseErrorMsg: string;
  noPickYet: boolean = false;
  dynamicPlaceholder: string = 'Select type first';
  displayEstDelivery: boolean;
  displayEstimatedDeliveryBusinessDays: boolean;
  categoriesSupportEtaBusinessDays: ShippingMethod[] = [
    ShippingMethod.DOMESTIC_LIVE_RATE,
    ShippingMethod.DOMESTIC,
    ShippingMethod.INTERNATIONAL_FIXED,
    ShippingMethod.INTERNATIONAL_LIVE_RATE,
    ShippingMethod.FIXED_LIVE_RATE,
    ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE,
  ];
  estimatedTimeLiveRateConversionOptions = [];
  showMultiShipDatePickerProfile = false;
  linkMultiShipDatePickerProfile = false;
  showPromiseOnlyWhenDateSelected = false;
  pdqIqBaseShippingMethods$ = this.store.select(fromCheckoutReducer.pdqIqBaseShippingMethods);
  deliveryPromisePdqIqBaseShippingMethodOptions = [];
  deliveryPromisePdqIqBaseShippingMethods = {};
  selectedDeliveryPromisePdqIqBaseShippingMethodZones = [];
  deliveryPromisePdqIqBaseShippingMethodZonesInitiallyLoaded = false;
  locationChangeSubscription$: Subscription;
  currentDate = new Date();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private checkoutService: CheckoutService,
    private store: Store<fromCheckoutReducer.State>,
  ) {}

  ngOnInit() {
    this.setEstimatedTimeLiveRateConversionOptions();
    this.pdqIqBaseShippingMethods$.pipe(takeUntil(this.destroy$)).subscribe((shippingMethodsDictionary) => {
      this.deliveryPromisePdqIqBaseShippingMethods = shippingMethodsDictionary;
      const shippingMethodKeys = Object.keys(shippingMethodsDictionary);
      this.deliveryPromisePdqIqBaseShippingMethodOptions = shippingMethodKeys.map((method) => ({
        label: method,
        value: method,
      }));
    });
  }

  get isPdqIqEnabled(): boolean {
    const shippedFromLocationId = this.formGroup?.get('shippedFromLocation')?.value;
    const shippedFromLocation = this.shop?.shopLocations?.find((x) => x.id === shippedFromLocationId);
    return shippedFromLocation?.countryCode === 'US';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['formGroup']?.currentValue && this.formGroup) {
        this.locationChangeSubscription$?.unsubscribe();
        this.locationChangeSubscription$ = this.formGroup
          .get('shippedFromLocation')
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.setEstimatedTimeLiveRateConversionOptions();
          });
        this.initDeliveryDisplayFormat();
        const linkedMultiShipDatePickerProfile = this.formGroup.get('linkedMultiShipDatePickerProfile')?.value;
        this.linkMultiShipDatePickerProfile = !!linkedMultiShipDatePickerProfile;
        this.showPromiseOnlyWhenDateSelected = !!linkedMultiShipDatePickerProfile?.showPromiseOnlyWhenDateSelected;
      }

      this.setEstimatedTimeLiveRateConversionOptions();
      if (this.activeCarrier) {
        this.isCarrierSupportSchedulePickup = carrierSupportSchedulePickup(this.activeCarrier);
      }
      this.initCarrierSupportSchedule();
      this.setPdqIqDefaultValues();
    }
  }

  private initDeliveryDisplayFormat(): void {
    const displayEstimatedDeliveryBusinessDays = this.formGroup?.value['displayEstimatedDeliveryBusinessDays'];
    if (
      displayEstimatedDeliveryBusinessDays &&
      !this.categoriesSupportEtaBusinessDays.includes(this.shippingMethodCategory)
    ) {
      this.formGroup?.get('displayEstimatedDeliveryBusinessDays')?.setValue(false);
    }
  }

  private setEstimatedTimeLiveRateConversionOptions(): void {
    const options = [ManualEstimatedConversionOption];
    switch (this.shippingMethodCategory) {
      case ShippingMethod.LOCAL:
        this.estimatedTimeLiveRateConversionOptions = [];
        break;

      case ShippingMethod.DOMESTIC:
        if (this.isPdqIqEnabled) {
          options.push(PDQIQEstimatedConversionOption);
        }
        options.push(LinkedCalendarEstimatedConversionOption);
        this.estimatedTimeLiveRateConversionOptions = [...options];
        break;

      case ShippingMethod.DOMESTIC_LIVE_RATE:
      case ShippingMethod.FIXED_LIVE_RATE:
        if (this.isCarrierSupportSchedulePickup && this.carrierSupportsLiveETA) {
          options.push(LiveEstimatedConverisonOption);
          if (this.isPdqIqEnabled) {
            options.push(PDQIQEstimatedConversionOption);
          }
        }
        this.estimatedTimeLiveRateConversionOptions = [...options];
        break;
      case ShippingMethod.INTERNATIONAL_LIVE_RATE:
      case ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE:
        if (this.isCarrierSupportSchedulePickup) {
          options.push(LiveEstimatedConverisonOption);
        }
        this.estimatedTimeLiveRateConversionOptions = [...options];
        break;

      default:
        this.estimatedTimeLiveRateConversionOptions = [ManualEstimatedConversionOption];
        break;
    }
    this.setMultiShipDatePickerProfileVisibility();
  }

  /* The multi-ship date-picker profile is an optional layer on top of the processing-time promise
     (it consumes processing time and adds its own blackouts), not a promise-calculation mode — so
     it lives outside the estimatedTimeConversions radio group, gated on the shop metadata flag. */
  private setMultiShipDatePickerProfileVisibility(): void {
    const isSupportedCategory = CATEGORIES_SUPPORTING_LINKED_DATE_PICKER_PROFILE.includes(this.shippingMethodCategory);
    this.showMultiShipDatePickerProfile = !!this.shop?.metadata?.showMultiShipmentExtension && isSupportedCategory;
  }

  initCarrierSupportSchedule(): void {
    this.checkoutService.onSetSelectedCarrier$.pipe(takeUntil(this.destroy$)).subscribe((carrier) => {
      this.isCarrierSupportSchedulePickup = carrierSupportSchedulePickup(carrier);
      this.carrierSupportsLiveETA = carrierSupportsLiveETA(carrier);
      this.setEstimatedTimeLiveRateConversionOptions();
      this.updateEstimatedTimeConversionsMethod(this.isCarrierSupportSchedulePickup);
    });
  }

  updateEstimatedTimeConversionsMethod(isCarrierSupportSchedulePickup: boolean) {
    const currentValue = this.formGroup.get('estimatedTimeConversions')?.value;
    const defaultEstimatedOption = this.estimatedTimeLiveRateConversionOptions[0];
    // On case we switched from to a carrier that does not support schedule pickup, we need to reset the estimated time conversions to the default option
    // if this options was no more in the options list
    if (!isCarrierSupportSchedulePickup) {
      const isSavedEstimatedTimeConversionExistsInOptions = this.estimatedTimeLiveRateConversionOptions.some(
        (option) => option.value === currentValue,
      );
      if (!isSavedEstimatedTimeConversionExistsInOptions) {
        // set to default available option.
        this.formGroup.get('estimatedTimeConversions')?.setValue(defaultEstimatedOption.value);
      }
    }
  }

  addDeliveryPromise(): void {
    if (this.deliveryPromiseDontCalculateOnFields?.length < 10) {
      if (this.deliveryPromiseTypeSelect && this.deliveryPromiseValue) {
        // Special validation for Custom Date - ensure both start and end dates are selected
        if (this.deliveryPromiseTypeSelect.type === Checkout.NonBusinessDaysTypeName.CUSTOM_DATE) {
          if (!this.deliveryPromiseValue.startDate || !this.deliveryPromiseValue.endDate) {
            this.showDeliveryPromiseErrorMsg = 'Please select both start and end dates for the custom date range';
            return;
          }
        }

        this.showDeliveryPromiseErrorMsg = undefined;
        if (
          this.deliveryPromiseTypeSelect.type != Checkout.NonBusinessDaysTypeName.CUSTOM_DAYS &&
          this.deliveryPromiseTypeSelect.type != Checkout.NonBusinessDaysTypeName.CUSTOM_DATE
        ) {
          const addLine = this.formBuilder.group({
            type: [this.deliveryPromiseTypeSelect.value],
            value: [this.deliveryPromiseValue.value],
          });
          this.deliveryPromiseDontCalculateOnFields.push(addLine);
          this.resetDeliveryPromiseDontCalculateOnFields();
        } else if (this.deliveryPromiseTypeSelect.type == Checkout.NonBusinessDaysTypeName.CUSTOM_DATE) {
          const addLine = this.formBuilder.group({
            type: [this.deliveryPromiseTypeSelect.value],
            value: [
              {
                startDate: this.deliveryPromiseValue.startDate,
                endDate: this.deliveryPromiseValue.endDate,
              },
            ],
          });
          this.deliveryPromiseDontCalculateOnFields.push(addLine);
          this.resetDeliveryPromiseDontCalculateOnFields();
        } else {
          const addLine = this.formBuilder.group({
            type: [this.deliveryPromiseTypeSelect.value],
            value: [
              {
                startDate: this.deliveryPromiseValue.startDate,
                endDate: this.deliveryPromiseValue.endDate,
              },
            ],
          });
          this.deliveryPromiseDontCalculateOnFields.push(addLine);
          this.resetDeliveryPromiseDontCalculateOnFields();
        }
      } else {
        this.showDeliveryPromiseErrorMsg = 'Pick from the options above to apply them';
      }
    }
  }

  onDeliveryPromiseSelect(event) {
    if (event) {
      this.noPickYet = true;
      if (event.value.value == Checkout.NonBusinessDaysTypeValue.CUSTOM_DAYS) {
        this.dynamicPlaceholder = 'Select from blackout dates';
        this.googleCalendarIdsList = this.formGroup.get('customBlackoutDates')['value'];
      } else if (event.value.value == Checkout.NonBusinessDaysTypeValue.WEEKEND_IN) {
        this.dynamicPlaceholder = 'Choose your weekend';
        this.googleCalendarIdsList = [...weekendDays];
      } else if (event.value.value == Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS) {
        this.dynamicPlaceholder = 'Choose your weekday';
        this.googleCalendarIdsList = this.filterOutUsedWeekdays();
      } else if (event.value.value == Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE) {
        this.dynamicPlaceholder = 'Select custom date';
        this.googleCalendarIdsList = [
          { name: Checkout.NonBusinessDaysTypeName.CUSTOM_DATE, value: Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE },
        ];
        // Initialize deliveryPromiseValue for custom date
        this.deliveryPromiseValue = {
          type: Checkout.NonBusinessDaysTypeName.CUSTOM_DATE,
          value: Checkout.NonBusinessDaysTypeValue.CUSTOM_DATE,
          name: Checkout.NonBusinessDaysTypeName.CUSTOM_DATE,
          startDate: null,
          endDate: null,
        };
      } else {
        this.dynamicPlaceholder = 'Select Holidays';
        this.googleCalendarIdsList = [...countryList()];
      }
    }
  }

  onDeliveryPromisePDQIQBaseShippingMethodSelect(event) {
    if (!event || !event.value) {
      return;
    }
    console.log('Selected PDQ IQ Base Shipping Method:', event.value);
    const selectedBaseShippingMethod = event.value;
    const deliveryPromisePdqIqPercentile = this.formGroup.get('deliveryPromisePdqIqPercentile').value;
    if (!deliveryPromisePdqIqPercentile) {
      return;
    }
    console.log('Selected PDQ IQ Base Shipping Method Percentile:', deliveryPromisePdqIqPercentile);
    this.selectedDeliveryPromisePdqIqBaseShippingMethodZones = [];
    this.deliveryPromisePdqIqBaseShippingMethodZonesInitiallyLoaded = true;
    setTimeout(() => {
      const zones = this.deliveryPromisePdqIqBaseShippingMethods[selectedBaseShippingMethod];
      this.selectedDeliveryPromisePdqIqBaseShippingMethodZones = [...zones].sort((a, b) => a.zone - b.zone);
    }, 1000);
  }

  onDeliveryPromisePDQIQPercentileSelect(event) {
    if (!event || !event.value) {
      return;
    }
    console.log('Selected PDQ IQ Base Shipping Method Percentile:', event.value);
    const deliveryPromisePdqIqBaseShippingMethod = this.formGroup.get('deliveryPromisePdqIqBaseShippingMethod').value;
    if (!deliveryPromisePdqIqBaseShippingMethod) {
      return;
    }
    const zones = this.deliveryPromisePdqIqBaseShippingMethods[deliveryPromisePdqIqBaseShippingMethod];
    this.selectedDeliveryPromisePdqIqBaseShippingMethodZones = [...zones].sort((a, b) => a.zone - b.zone);
  }

  filterOutUsedWeekdays() {
    let weekdaysOptions = this.weekdayOptions;
    const deliveryPromiseNotCalculatedArray = this.deliveryPromiseDontCalculateOnFields.value;
    if (deliveryPromiseNotCalculatedArray?.length) {
      const weekdayRules = deliveryPromiseNotCalculatedArray.filter(
        (d) => d.type === Checkout.NonBusinessDaysTypeValue.WEEKDAY_IS,
      );
      if (weekdayRules?.length) {
        const usedWeekdays: number[] = weekdayRules.map((r) => r.value);
        weekdaysOptions = weekdaysOptions.filter((val) => !usedWeekdays.includes(val.value));
      }
    }
    return weekdaysOptions;
  }

  get deliveryPromiseDontCalculateOnFields(): UntypedFormArray {
    return this.formGroup.get('deliveryPromiseDontCalculateOn') as UntypedFormArray;
  }

  resetDeliveryPromiseDontCalculateOnFields(): void {
    this.deliveryPromiseTypeSelect = undefined;
    this.deliveryPromiseValue = undefined;
  }

  convertDeliveryPromiseType(str: string): string {
    return convertDeliveryPromiseType(str);
  }

  convertDeliveryPromiseValue(str: string, type: string): string {
    return convertDeliveryPromiseValues(str, type);
  }

  removeDeliveryPromise(index: number): void {
    if (this.deliveryPromiseDontCalculateOnFields?.length >= 0) {
      this.deliveryPromiseDontCalculateOnFields.removeAt(index);
    }
  }

  onOpenExpansion(section: string): boolean {
    this.onOpenExpansionEmit.emit(section);
    return true;
  }

  onCloseExpansion(): void {
    this.onCloseExpansionEmit.emit();
  }

  isExpandedNeeded(section: string): boolean {
    return this.accordionActiveSection.some((value) => value == section);
  }

  getPdqIqBaseMethodDefaultOption(currentValue: string): DropdownOption {
    return this.deliveryPromisePdqIqBaseShippingMethodOptions?.find((x) => x.value === currentValue);
  }

  checkEstimatedTimeDisabled(estimatedTimeValue: string): boolean {
    if (estimatedTimeValue === ICheckoutEstimatedTimeConversion.PDQIQ) {
      return this.deliveryPromisePdqIqBaseShippingMethodOptions.length === 0;
    }
    return false;
  }

  get deliveryDisplayFormatTooltip(): string {
    return !!this.runningABTest
      ? 'Disabled due running A/B test'
      : 'Choose how delivery time is shown to customers during checkout';
  }

  get isDontShowTabActive(): boolean {
    return (
      !this.formGroup?.value['displayEstDelivery'] && !this.formGroup?.value['displayEstimatedDeliveryBusinessDays']
    );
  }

  get isDeliveryTimeTabActive(): boolean {
    return !!this.formGroup?.value['displayEstimatedDeliveryBusinessDays'];
  }

  get isDateRangeTabActive(): boolean {
    return !!this.formGroup?.value['displayEstDelivery'];
  }

  onDeliveryDisplayFormatChange(value: string): void {
    const isDateRange = value === 'date_range';
    const isDeliveryTime = value === 'delivery_time';
    this.formGroup.get('displayEstDelivery')?.setValue(isDateRange);
    this.formGroup.get('displayEstimatedDeliveryBusinessDays')?.setValue(isDeliveryTime);
  }

  onEstimateTimeRateConversionChange(value: string): void {
    if (value === ICheckoutEstimatedTimeConversion.PDQIQ) {
      const isPdqIqPopulated =
        this.formGroup?.get('deliveryPromisePdqIqBaseShippingMethod')?.value &&
        this.formGroup?.get('deliveryPromisePdqIqPercentile')?.value;
      if (!isPdqIqPopulated) {
        this.setPdqIqDefaultValues();
      }
      // MAXX (PDQIQ) drives the promise from its prediction, so the manual transit days never
      // apply. Always clear them on switch — including when MAXX fields are already populated —
      // so stale values (e.g. left over from a previous Fixed-promise config) aren't persisted.
      this.clearTransitDays();
    } else {
      this.clearPdqIqValues();
    }
    if (value !== ICheckoutEstimatedTimeConversion.LINKED_CALENDAR) {
      this.formGroup.get('linkedDatePickerExtensionId').setValue(null);
    }
  }

  clearPdqIqValues() {
    this.formGroup.get('deliveryPromisePdqIqBaseShippingMethod').setValue(null);
    this.formGroup.get('deliveryPromisePdqIqPercentile').setValue(null);
    this.formGroup.get('maxxAiDeliveryPromiseEnabled')?.setValue(false);
    this.formGroup.get('maxxAiDeliveryPromiseMinCap')?.setValue(null);
  }

  clearTransitDays() {
    this.formGroup.get('deliveryPromiseDeliveryTimeSettings')?.get('minTransitDays')?.setValue(null);
    this.formGroup.get('deliveryPromiseDeliveryTimeSettings')?.get('maxTransitDays')?.setValue(null);
    this.formGroup.get('deliveryPromiseDeliveryTimeSettings')?.get('minDays')?.setValue(null);
    this.formGroup.get('deliveryPromiseDeliveryTimeSettings')?.get('maxDays')?.setValue(null);
  }

  clearProcessingDays() {
    this.formGroup.get('minProcessingDays').setValue(null);
    this.formGroup.get('maxProcessingDays').setValue(null);
  }

  setPdqIqDefaultValues() {
    const deliveryPromisePdqIqBaseShippingMethod = this.formGroup.get('deliveryPromisePdqIqBaseShippingMethod').value;
    const deliveryPromisePdqIqPercentile = this.formGroup.get('deliveryPromisePdqIqPercentile').value;
    if (!deliveryPromisePdqIqPercentile) {
      const PDQIQ_DEFAULT_PERCENTILE = 80;
      this.formGroup
        .get('deliveryPromisePdqIqPercentile')
        .setValue(this.deliveryPromisePdqIqPercentileOptions.find((x) => x.value === PDQIQ_DEFAULT_PERCENTILE)?.value);
    }
    if (deliveryPromisePdqIqBaseShippingMethod) {
      const zones = this.deliveryPromisePdqIqBaseShippingMethods[deliveryPromisePdqIqBaseShippingMethod];
      if (zones?.length) {
        this.selectedDeliveryPromisePdqIqBaseShippingMethodZones = [...zones].sort((a, b) => a.zone - b.zone);
      }
    }
  }

  getAdditionalDeliveryPromiseText(): string {
    if (
      [
        ShippingMethod.DOMESTIC_LIVE_RATE,
        ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE,
        ShippingMethod.FIXED_LIVE_RATE,
        ShippingMethod.INTERNATIONAL_LIVE_RATE,
      ].includes(this.shippingMethodCategory)
    ) {
      return '\n Delivery promise text will be hidden if carrier does not provide a valid ETA';
    }
    return '';
  }

  getLinkedDatePickerExtensionDefaultValue(value: number): DropdownOption {
    const availableCalendar = this.linkedDatePickerExtensionOptions.find((x) => x.value === value);
    if (!availableCalendar) {
      return LINKED_CALENDAR_DEFAULT_OPTION;
    }
    return availableCalendar;
  }

  onToggleMultiShipDatePickerProfileLink(checked: boolean): void {
    this.linkMultiShipDatePickerProfile = checked;
    if (!checked) {
      this.showPromiseOnlyWhenDateSelected = false;
      this.formGroup.get('linkedMultiShipDatePickerProfile')?.setValue(null);
    }
  }

  /* ponytail: the flag rides inside @linkedMultiShipDatePickerProfile instead of its own checkout
     column, so no shared-interface/migration change is needed for the hotfix. */
  onTogglePromiseOnlyWhenDateSelected(checked: boolean): void {
    this.showPromiseOnlyWhenDateSelected = checked;
    const linkedProfileControl = this.formGroup.get('linkedMultiShipDatePickerProfile');
    if (!linkedProfileControl?.value) {
      return;
    }
    linkedProfileControl.setValue({ ...linkedProfileControl.value, showPromiseOnlyWhenDateSelected: checked });
  }

  /* The dropdown can only carry a primitive value, so a profile is keyed as `${extensionId}:${profileName}`
     while the form keeps the persisted { multiShipExtensionId, profileName } object. */
  onMultiShipDatePickerProfilePick(option: DropdownOption): void {
    const compositeKey = option?.value as string;
    if (!compositeKey) {
      this.formGroup.get('linkedMultiShipDatePickerProfile')?.setValue(null);
      return;
    }
    const separatorIndex = compositeKey.indexOf(':');
    this.formGroup.get('linkedMultiShipDatePickerProfile')?.setValue({
      multiShipExtensionId: Number(compositeKey.slice(0, separatorIndex)),
      profileName: compositeKey.slice(separatorIndex + 1),
      showPromiseOnlyWhenDateSelected: this.showPromiseOnlyWhenDateSelected,
    });
  }

  getMultiShipDatePickerProfileDefaultValue(
    value: { multiShipExtensionId: number; profileName: string } | null,
  ): DropdownOption {
    if (!value) {
      return null;
    }
    const compositeKey = `${value.multiShipExtensionId}:${value.profileName}`;
    return this.multiShipDatePickerProfileOptions.find((option) => option.value === compositeKey);
  }

  handleCustomDateRangeChange({ start, end }) {
    this.deliveryPromiseValue = {
      ...this.deliveryPromiseValue,
      startDate: start,
      endDate: end,
    };
  }
}
